import React,{useState,useEffect,useCallback,useMemo,useRef} from 'react';
import {userAPI,branchAPI,auditAPI,uploadAPI} from '../utils/api';
import {PageHeader,SearchBar,DataTable,Badge,Modal,FormInput,FilterButtons,fmtDate,fmt} from '../components/common';
import {useLang} from '../i18n';
import {useAuth} from '../context/AuthContext';
import {
  Plus,Edit,Trash2,ToggleLeft,ToggleRight,Shield,Users,ClipboardList,LogIn,
  Eye,Building2,ShieldOff,AlertTriangle,UserCheck,Lock,Info,
  CheckCircle,XCircle,LayoutGrid,ChevronDown,ChevronUp,Crown,
  Camera,Upload,Paperclip,FileText,RefreshCw,Download,X as XIcon,Image as ImageIcon
} from 'lucide-react';
import toast from 'react-hot-toast';

/* ─── Permission groups (all permissions including new ones) ─── */
const OWNER_ONLY_PERMS=[
  'business.edit','roles.manage','settings.edit',
  'import.data','audit.view','notifications.manage',
];

const PERM_GROUPS={
  'Dashboard':           ['dashboard.view','dashboard.analytics'],
  'Branches':            ['branches.view','branches.create','branches.edit','branches.delete','locations.manage'],
  'Users':               ['users.view','users.create','users.edit','users.delete','roles.manage'],
  'Customers':           ['customers.view','customers.create','customers.edit','customers.delete','customer_groups.manage','customer_loyalty.manage','customers.export'],
  'Vendors':             ['vendors.view','vendors.create','vendors.edit','vendors.delete','vendors.export'],
  'Products':            ['products.view','products.create','products.edit','products.delete','categories.manage','product_prices.manage','cost_price.view'],
  'Inventory/Stock':     ['inventory.view','inventory.adjust','inventory.transfer','inventory.approve','inventory.export','cost_price.view'],
  'Purchase Orders':     ['purchase_orders.view','purchase_orders.create','purchase_orders.edit','purchase_orders.delete','purchase_orders.receive'],
  'Stock Transfers':     ['stock_transfers.view','stock_transfers.create','stock_transfers.approve','stock_transfers.receive','stock_transfers.delete'],
  'Item Commissions':    ['item_commissions.view_own','item_commissions.view','item_commissions.manage','item_commissions.manage_all'],
  'Sale Orders':         ['orders.view','orders.create','orders.edit','orders.delete','orders.approve','orders.cancel'],
  'Sale Invoices':       ['invoices.view','invoices.create','invoices.edit','invoices.delete','invoices.payment','invoices.approve'],
  'POS Terminal':        ['pos.access','pos.discount','pos.refund','pos.close_register'],
  'POS Sessions':        ['pos.open_session','pos.close_session','pos.sessions_view','pos.sessions_report'],
  'Commissions':         ['commissions.view','commissions.manage','commissions.approve'],
  'Promotions':          ['promotions.view','promotions.manage'],
  'Payments':            ['payments.view','payments.manage','payments.refund'],
  'Expenses':            ['expenses.view','expenses.create','expenses.edit','expenses.delete','expenses.approve','reports.expenses'],
  'Reports (POS)':       ['reports.pos_summary','reports.pos_sessions','reports.cash_register'],
  'Reports (Sales)':     ['reports.orders','reports.invoices','reports.customers','reports.product_sales'],
  'Reports (Inventory)': ['reports.stock','reports.vendors','reports.purchase_orders','reports.stock_transfers'],
  'Reports (Finance)':   ['reports.vendor_payments','reports.payments','reports.commissions','reports.commission'],
  'Reports (All)':       ['reports.sales','reports.inventory','reports.financial','reports.export'],
  'Settings':            ['settings.view','settings.edit','tax.manage','currency.manage'],
  'System':              ['import.data','export.data','audit.view','notifications.manage','register.open','register.close','register.cash_io'],
};
const ALL_PERMS=Object.values(PERM_GROUPS).flat();

/* Role templates mirroring server */
const ROLE_DEFAULTS={
  'Owner':ALL_PERMS,
  'Branch Manager':ALL_PERMS.filter(p=>!OWNER_ONLY_PERMS.includes(p)),
  'Sales Rep':['dashboard.view','customers.view','customers.create','customers.edit','products.view','inventory.view','orders.view','orders.create','orders.edit','invoices.view','invoices.create','pos.access','pos.discount','pos.open_session','pos.close_session','commissions.view','payments.view','item_commissions.view_own','register.open','register.close','reports.commissions','expenses.view','expenses.create','reports.expenses'],
  'Cashier':['dashboard.view','customers.view','products.view','inventory.view','pos.access','pos.open_session','pos.close_session','payments.view','item_commissions.view_own','register.open','register.close','register.cash_io'],
  'Inventory Staff':['dashboard.view','products.view','products.create','products.edit','categories.manage','inventory.view','inventory.adjust','inventory.transfer','inventory.export','purchase_orders.view','purchase_orders.create','purchase_orders.receive','stock_transfers.view','stock_transfers.create','stock_transfers.receive','cost_price.view','vendors.view','reports.inventory','reports.stock','reports.vendors','reports.product_sales','reports.purchase_orders','reports.stock_transfers'],
  'Accountant':['dashboard.view','dashboard.analytics','customers.view','invoices.view','invoices.create','invoices.edit','invoices.payment','payments.view','payments.manage','payments.refund','commissions.view','commissions.approve','purchase_orders.view','cost_price.view','item_commissions.view','reports.sales','reports.commission','reports.financial','reports.orders','reports.invoices','reports.customers','reports.payments','reports.commissions','reports.vendor_payments','export.data','currency.manage','expenses.view','expenses.create','expenses.edit','expenses.approve','reports.expenses'],
  'Supervisor':['dashboard.view','dashboard.analytics','branches.view','customers.view','products.view','inventory.view','inventory.approve','purchase_orders.view','purchase_orders.create','purchase_orders.edit','purchase_orders.receive','stock_transfers.view','stock_transfers.create','stock_transfers.approve','stock_transfers.receive','cost_price.view','orders.view','orders.approve','orders.cancel','invoices.view','invoices.approve','pos.access','pos.discount','pos.refund','pos.open_session','pos.close_session','pos.sessions_view','pos.sessions_report','commissions.view','item_commissions.view','item_commissions.manage','reports.sales','reports.inventory','reports.pos_summary','reports.pos_sessions','reports.cash_register','reports.orders','reports.invoices','reports.customers','reports.product_sales','reports.stock','reports.vendors','reports.purchase_orders','reports.stock_transfers','reports.commissions','expenses.view','expenses.create','reports.expenses'],
  'POS Manager':['dashboard.view','customers.view','customers.create','customers.edit','products.view','inventory.view','pos.access','pos.discount','pos.refund','pos.close_register','pos.open_session','pos.close_session','pos.sessions_view','pos.sessions_report','payments.view','payments.manage','item_commissions.view','item_commissions.manage','register.open','register.close','register.cash_io','reports.sales','reports.pos_summary','reports.pos_sessions','reports.cash_register','reports.customers','reports.payments'],
  'Sales Viewer':['dashboard.view','customers.view','products.view','inventory.view','orders.view','invoices.view','payments.view','reports.sales'],
  'Stock Viewer':['dashboard.view','products.view','inventory.view','inventory.export','purchase_orders.view','stock_transfers.view','vendors.view','reports.inventory'],
};

/* Module access matrix — which permission = access to which module */
const MODULE_MATRIX=[
  {module:'Dashboard',          perm:'dashboard.view',        sub:'dashboard.analytics',      subLabel:'Analytics'},
  {module:'POS Terminal',       perm:'pos.access',            sub:'pos.discount',             subLabel:'Discounts'},
  {module:'Open POS Session',   perm:'pos.open_session',      sub:'register.open',            subLabel:'Register Open'},
  {module:'Close POS Session',  perm:'pos.close_session',     sub:'pos.close_register',       subLabel:'Force Close'},
  {module:'POS Sessions View',  perm:'pos.sessions_view',     sub:'pos.sessions_report',      subLabel:'Reports'},
  {module:'Sale Orders',        perm:'orders.view',           sub:'orders.create',            subLabel:'Create'},
  {module:'Sale Invoices',      perm:'invoices.view',         sub:'invoices.create',          subLabel:'Create'},
  {module:'Customers',          perm:'customers.view',        sub:'customers.create',         subLabel:'Create/Edit'},
  {module:'Vendors',            perm:'vendors.view',          sub:'vendors.create',           subLabel:'Create/Edit'},
  {module:'Products',           perm:'products.view',         sub:'products.create',          subLabel:'Create/Edit'},
  {module:'Cost Price & Stock Value', perm:'cost_price.view', ownerOnly:false},
  {module:'Inventory',          perm:'inventory.view',        sub:'inventory.adjust',         subLabel:'Adjust'},
  {module:'Purchase Orders',    perm:'purchase_orders.view',  sub:'purchase_orders.create',   subLabel:'Create'},
  {module:'PO Receive Stock',   perm:'purchase_orders.receive',sub:'purchase_orders.edit',    subLabel:'Edit'},
  {module:'Stock Transfers',    perm:'stock_transfers.view',  sub:'stock_transfers.create',   subLabel:'Create'},
  {module:'Transfer Approve',   perm:'stock_transfers.approve',sub:'stock_transfers.receive', subLabel:'Receive'},
  {module:'Item Commissions (own)', perm:'item_commissions.view_own', ownerOnly:false},
  {module:'Item Commissions (branch)', perm:'item_commissions.view', sub:'item_commissions.manage', subLabel:'Manage'},
  {module:'Item Commissions (all)',    perm:'item_commissions.manage_all', ownerOnly:false},
  {module:'Commissions',        perm:'commissions.view',      sub:'commissions.manage',       subLabel:'Manage'},
  {module:'Payments',           perm:'payments.view',         sub:'payments.manage',          subLabel:'Manage'},
  {module:'Expenses',           perm:'expenses.view',         sub:'expenses.create',          subLabel:'Create/Edit'},
  {module:'Expenses — Approve', perm:'expenses.approve',      ownerOnly:false},
  {module:'Expenses — Delete',  perm:'expenses.delete',       ownerOnly:false},
  {module:'Reports — Expenses', perm:'reports.expenses',      ownerOnly:false},
  {module:'Reports — POS Summary',       perm:'reports.pos_summary',    ownerOnly:false},
  {module:'Reports — POS Sessions',      perm:'reports.pos_sessions',   ownerOnly:false},
  {module:'Reports — Cash Register',     perm:'reports.cash_register',  ownerOnly:false},
  {module:'Reports — Orders',            perm:'reports.orders',         ownerOnly:false},
  {module:'Reports — Invoices',          perm:'reports.invoices',       ownerOnly:false},
  {module:'Reports — Customers',         perm:'reports.customers',      ownerOnly:false},
  {module:'Reports — Product Sales',     perm:'reports.product_sales',  ownerOnly:false},
  {module:'Reports — Stock Levels',      perm:'reports.stock',          ownerOnly:false},
  {module:'Reports — Vendors',           perm:'reports.vendors',        ownerOnly:false},
  {module:'Reports — Purchase Orders',   perm:'reports.purchase_orders',ownerOnly:false},
  {module:'Reports — Stock Transfers',   perm:'reports.stock_transfers',ownerOnly:false},
  {module:'Reports — Vendor Payments',   perm:'reports.vendor_payments',ownerOnly:false},
  {module:'Reports — Payments',          perm:'reports.payments',       ownerOnly:false},
  {module:'Reports — Item Commissions',  perm:'reports.commissions',    ownerOnly:false},
  {module:'Reports (broad access)',       perm:'reports.sales',          sub:'reports.financial', subLabel:'Financial'},
  {module:'Users/Roles',        perm:'users.view',            sub:'users.create',             subLabel:'Create'},
  {module:'Settings',           perm:'settings.view',         sub:'settings.edit',            subLabel:'Edit'},
  {module:'Audit Logs',         perm:'audit.view',            ownerOnly:true},
];

const ROLE_BADGE={
  Owner:'accent','Branch Manager':'primary','Sales Rep':'info',Cashier:'success',
  Accountant:'warning',Supervisor:'primary','Inventory Staff':'warning',
  'POS Manager':'info','Sales Viewer':'success','Stock Viewer':'warning',
};

const BranchTag=({branch,locked})=>(
  <span style={{display:'inline-flex',alignItems:'center',gap:4,background:locked?'var(--dm)':'var(--pm)',color:locked?'var(--danger)':'var(--primary)',padding:'2px 8px',borderRadius:6,fontSize:11,fontWeight:700}}>
    {locked?<Lock size={10}/>:<Building2 size={10}/>}
    {branch?.name||'All Branches'}
  </span>
);

const OwnerOnlyBadge=()=>(
  <span style={{display:'inline-flex',alignItems:'center',gap:3,background:'#fef3c7',color:'#d97706',
    padding:'1px 6px',borderRadius:4,fontSize:9,fontWeight:700,letterSpacing:.3}}>
    <Crown size={8}/> OWNER ONLY
  </span>
);

/* ─── Permission checkbox with owner-only indicator ─── */
const PermCheck=({perm,checked,onChange,roleForm})=>{
  const isOwnerOnly=OWNER_ONLY_PERMS.includes(perm);
  const label=perm.split('.')[1];
  return(
    <label style={{display:'flex',alignItems:'center',gap:6,padding:'3px 0',cursor:'pointer',
      fontSize:12,color:checked?'var(--text)':'var(--td)',opacity:isOwnerOnly?0.7:1}}>
      <input type="checkbox" checked={checked} onChange={onChange} style={{accentColor:'var(--primary)'}}/>
      <span>{label}</span>
      {isOwnerOnly&&<OwnerOnlyBadge/>}
    </label>
  );
};

const UserCard=({u,isOwner,currentBranchId,can,onEdit,onToggle,onDelete})=>{
  const sameB=!currentBranchId||u.branch_id===currentBranchId||isOwner;
  const roleName=u.role?.name||'No Role';
  const initials=`${u.first_name?.[0]||''}${u.last_name?.[0]||''}`.toUpperCase();
  const docCount=(u.documents||[]).length;
  return(
    <div style={{background:'var(--surface)',border:`1px solid ${!sameB?'var(--danger)':u.is_active?'var(--border)':'var(--border)'}`,borderRadius:12,padding:16,opacity:!sameB?0.55:1,position:'relative',overflow:'hidden'}}>
      {!sameB&&<div style={{position:'absolute',top:0,left:0,right:0,height:3,background:'var(--danger)'}}/>}
      <div style={{display:'flex',alignItems:'flex-start',gap:12,marginBottom:12}}>
        {/* Avatar — photo or initials */}
        <div style={{width:48,height:48,borderRadius:12,background:u.is_active?'var(--pm)':'var(--s3)',
          display:'flex',alignItems:'center',justifyContent:'center',
          fontSize:15,fontWeight:800,color:u.is_active?'var(--primary)':'var(--td)',
          flexShrink:0,overflow:'hidden',border:'2px solid var(--border)'}}>
          {u.avatar_url
            ?<img src={u.avatar_url?.startsWith('http')?u.avatar_url:(window.location.protocol+'//'+window.location.hostname+':5000'+u.avatar_url)} alt={initials} style={{width:'100%',height:'100%',objectFit:'cover'}}/>
            :(initials||'?')}
        </div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:700,fontSize:14,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{u.first_name} {u.last_name}</div>
          <div style={{fontSize:11,color:'var(--td)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{u.email}</div>
          <div style={{marginTop:4,display:'flex',gap:5,flexWrap:'wrap',alignItems:'center'}}>
            <span style={{fontSize:10,fontWeight:700,color:`var(--${ROLE_BADGE[roleName]||'primary'})`,background:`var(--${ROLE_BADGE[roleName]||'primary'}m)`,padding:'2px 7px',borderRadius:4}}>{roleName}</span>
            <Badge variant={u.is_active?'success':'danger'}>{u.is_active?'Active':'Inactive'}</Badge>
            {docCount>0&&<span style={{fontSize:10,fontWeight:700,color:'var(--accent)',background:'var(--am)',padding:'2px 7px',borderRadius:4,display:'flex',alignItems:'center',gap:3}}>
              <Paperclip size={9}/>{docCount} doc{docCount!==1?'s':''}
            </span>}
          </div>
        </div>
      </div>
      <div style={{marginBottom:12}}>
        <BranchTag branch={u.branch} locked={!sameB}/>
        {u.commission_rate>0&&<span style={{marginLeft:6,fontSize:11,color:'var(--warning)',fontWeight:600}}>{u.commission_rate}% comm.</span>}
        {u.id_number&&<div style={{marginTop:6,display:'flex',alignItems:'center',gap:5,fontSize:10,color:'var(--td)'}}>
          <span>🪪</span>
          <span style={{fontFamily:'monospace',color:'var(--tm)',fontWeight:600}}>{u.id_number}</span>
          {u.id_type&&<span style={{color:'var(--td)'}}>({u.id_type.replace('_',' ')})</span>}
          {u.id_expiry&&<span style={{color:new Date(u.id_expiry)<new Date()?'var(--danger)':'var(--td)'}}>
            · exp {new Date(u.id_expiry).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'})}
            {new Date(u.id_expiry)<new Date()&&' ⚠ Expired'}
          </span>}
        </div>}
      </div>
      {!sameB&&<div style={{fontSize:11,color:'var(--danger)',display:'flex',alignItems:'center',gap:4,marginBottom:10,padding:'6px 8px',background:'var(--dm)',borderRadius:6}}><ShieldOff size={11}/> Outside your branch — view only</div>}
      <div style={{display:'flex',gap:6,borderTop:'1px solid var(--border)',paddingTop:10}}>
        {sameB&&can.edit&&<button className="btn btn-ghost btn-sm" style={{flex:1}} onClick={()=>onEdit(u)}><Edit size={12}/> Edit</button>}
        {sameB&&can.edit&&<button className="btn btn-ghost btn-sm" onClick={()=>onToggle(u)} title={u.is_active?'Deactivate':'Activate'}>{u.is_active?<ToggleRight size={14} color="var(--success)"/>:<ToggleLeft size={14}/>}</button>}
        {sameB&&can.delete&&<button className="btn btn-danger btn-sm" onClick={()=>onDelete(u)}><Trash2 size={12}/></button>}
        {!sameB&&<div style={{flex:1,fontSize:11,color:'var(--td)',display:'flex',alignItems:'center',gap:4}}><Lock size={11}/> Restricted</div>}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   MODULE ACCESS MATRIX TAB
═══════════════════════════════════════════ */
function ModuleMatrixTab({roles}){
  const[expanded,setExpanded]=useState(null);

  const roleList=useMemo(()=>roles.slice(0,10),[roles]);

  const getAccess=(role,perm)=>{
    const perms=role.permissions||[];
    return perms.includes(perm)?'full':false;
  };

  const AccessCell=({role,perm,ownerOnly})=>{
    const has=(role.permissions||[]).includes(perm);
    const isOwnerRole=role.name==='Owner'||(role.permissions||[]).length>=ALL_PERMS.length-2;
    if(ownerOnly){
      return isOwnerRole
        ?<div style={{display:'flex',justifyContent:'center'}}><Crown size={14} color="#d97706"/></div>
        :<div style={{display:'flex',justifyContent:'center'}}><XCircle size={13} color="var(--border)"/></div>;
    }
    return(
      <div style={{display:'flex',justifyContent:'center'}}>
        {has
          ?<CheckCircle size={13} color="var(--success)"/>
          :<XCircle size={13} color="var(--border)"/>}
      </div>
    );
  };

  return(
    <div>
      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16,padding:'10px 14px',
        background:'var(--pm)',borderRadius:10,fontSize:13}}>
        <Info size={15} color="var(--primary)"/>
        <span style={{color:'var(--primary)',fontWeight:600}}>Module Access Matrix</span>
        <span style={{color:'var(--td)',fontSize:12}}>— Shows which modules each role can access. <Crown size={10} color="#d97706"/> = Owner only.</span>
      </div>

      <div style={{overflowX:'auto',borderRadius:12,border:'1px solid var(--border)'}}>
        <table style={{width:'100%',borderCollapse:'collapse',minWidth:700}}>
          <thead>
            <tr style={{background:'var(--s2)'}}>
              <th style={{padding:'10px 14px',textAlign:'left',fontSize:11,fontWeight:700,color:'var(--td)',
                textTransform:'uppercase',letterSpacing:.4,borderBottom:'1px solid var(--border)',
                position:'sticky',left:0,background:'var(--s2)',zIndex:2,minWidth:140}}>
                Module
              </th>
              {roleList.map(r=>(
                <th key={r.id} style={{padding:'8px 10px',textAlign:'center',fontSize:11,fontWeight:700,
                  color:`var(--${ROLE_BADGE[r.name]||'primary'})`,
                  borderBottom:'1px solid var(--border)',minWidth:90,whiteSpace:'nowrap',
                  borderLeft:'1px solid var(--border)'}}>
                  <div>{r.name}</div>
                  <div style={{fontSize:9,fontWeight:400,color:'var(--td)',marginTop:2}}>
                    {(r.permissions||[]).length}/{ALL_PERMS.length}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {MODULE_MATRIX.map((row,i)=>(
              <tr key={row.module}
                style={{background:i%2===0?'var(--surface)':'var(--s2)',
                  borderBottom:'1px solid var(--border)'}}>
                <td style={{padding:'9px 14px',position:'sticky',left:0,
                  background:i%2===0?'var(--surface)':'var(--s2)',zIndex:1,borderRight:'1px solid var(--border)'}}>
                  <div style={{fontSize:13,fontWeight:700}}>{row.module}</div>
                  {row.sub&&<div style={{fontSize:10,color:'var(--td)',marginTop:1}}>incl. {row.subLabel}</div>}
                  {row.ownerOnly&&<OwnerOnlyBadge/>}
                </td>
                {roleList.map(r=>(
                  <td key={r.id} style={{padding:'9px 10px',textAlign:'center',borderLeft:'1px solid var(--border)'}}>
                    <AccessCell role={r} perm={row.perm} ownerOnly={row.ownerOnly}/>
                    {row.sub&&!row.ownerOnly&&(
                      <div style={{marginTop:3}}>
                        <AccessCell role={r} perm={row.sub}/>
                      </div>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div style={{display:'flex',gap:16,marginTop:12,flexWrap:'wrap',fontSize:12,color:'var(--td)'}}>
        <div style={{display:'flex',alignItems:'center',gap:6}}><CheckCircle size={13} color="var(--success)"/> Has access</div>
        <div style={{display:'flex',alignItems:'center',gap:6}}><XCircle size={13} color="var(--border)"/> No access</div>
        <div style={{display:'flex',alignItems:'center',gap:6}}><Crown size={13} color="#d97706"/> Owner only</div>
        <div style={{color:'var(--td)'}}>Top row = view permission · Bottom row = write/manage permission</div>
      </div>

      {/* Per-role detail cards */}
      <div style={{marginTop:24}}>
        <div style={{fontSize:13,fontWeight:800,marginBottom:12}}>Role Details & Branch Scope</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:10}}>
          {roles.map(role=>{
            const pc=(role.permissions||[]).length;
            const isExpanded=expanded===role.id;
            const ownerLevel=pc>=ALL_PERMS.length-2;
            const scopeNote=OWNER_ONLY_PERMS.filter(p=>(role.permissions||[]).includes(p)).length>0
              ?'Owner / global access'
              :'Branch-scoped (inherits user branch)';
            return(
              <div key={role.id} style={{background:'var(--surface)',border:'1.5px solid var(--border)',
                borderRadius:11,overflow:'hidden'}}>
                <div style={{display:'flex',alignItems:'center',gap:10,padding:'12px 14px',cursor:'pointer'}}
                  onClick={()=>setExpanded(isExpanded?null:role.id)}>
                  <div style={{width:36,height:36,borderRadius:9,background:`var(--${ROLE_BADGE[role.name]||'primary'}m)`,
                    display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                    {ownerLevel?<Crown size={16} color="#d97706"/>:<Shield size={16} color={`var(--${ROLE_BADGE[role.name]||'primary'})`}/>}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:800}}>{role.name}</div>
                    <div style={{fontSize:11,color:'var(--td)',marginTop:2}}>{scopeNote} · {pc} permissions</div>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:6}}>
                    <div style={{fontSize:12,fontWeight:700,color:ownerLevel?'#d97706':pc>30?'var(--success)':pc>15?'var(--info)':'var(--warning)'}}>
                      {ownerLevel?'Full':'Partial'}
                    </div>
                    {isExpanded?<ChevronUp size={14} color="var(--td)"/>:<ChevronDown size={14} color="var(--td)"/>}
                  </div>
                </div>
                {isExpanded&&(
                  <div style={{borderTop:'1px solid var(--border)',padding:'10px 14px',background:'var(--s2)'}}>
                    {/* Branch scope indicator */}
                    <div style={{display:'flex',alignItems:'center',gap:6,padding:'7px 10px',
                      borderRadius:8,marginBottom:10,fontSize:12,
                      background:ownerLevel?'#fef3c722':'var(--pm)',
                      color:ownerLevel?'#d97706':'var(--primary)'}}>
                      {ownerLevel?<Crown size={12}/>:<Building2 size={12}/>}
                      <strong>Scope:</strong> {ownerLevel?'All branches (owner-level)':'Restricted to assigned branch only'}
                    </div>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))',gap:6}}>
                      {Object.entries(PERM_GROUPS).map(([group,perms])=>{
                        const has=perms.filter(p=>(role.permissions||[]).includes(p)).length;
                        if(has===0)return null;
                        return(
                          <div key={group} style={{padding:'6px 8px',borderRadius:7,
                            background:has===perms.length?'var(--sm)':'var(--wm)',
                            border:`1px solid ${has===perms.length?'rgba(34,197,94,.2)':'rgba(245,158,11,.2)'}`}}>
                            <div style={{fontSize:10,fontWeight:700,
                              color:has===perms.length?'var(--success)':'var(--warning)',marginBottom:2}}>
                              {group}
                            </div>
                            <div style={{fontSize:10,color:'var(--td)'}}>{has}/{perms.length} permissions</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════ */
/* ── Photo Capture Component ───────────────────────────────────── */
function PhotoCapture({value, onChange}){
  const videoRef=useRef(null);
  const canvasRef=useRef(null);
  const fileRef=useRef(null);
  const[streaming,setStreaming]=useState(false);
  const[uploading,setUploading]=useState(false);

  const startCamera=async()=>{
    try{
      const stream=await navigator.mediaDevices.getUserMedia({video:{facingMode:'user'}});
      if(videoRef.current){videoRef.current.srcObject=stream;setStreaming(true);}
    }catch{toast.error('Camera not available');}
  };
  const stopCamera=()=>{
    const stream=videoRef.current?.srcObject;
    stream?.getTracks().forEach(t=>t.stop());
    if(videoRef.current) videoRef.current.srcObject=null;
    setStreaming(false);
  };
  const snap=async()=>{
    const v=videoRef.current;const c=canvasRef.current;
    if(!v||!c)return;
    c.width=v.videoWidth;c.height=v.videoHeight;
    c.getContext('2d').drawImage(v,0,0);
    c.toBlob(async blob=>{
      if(!blob)return;
      setUploading(true);
      try{
        const file=new File([blob],'avatar.jpg',{type:'image/jpeg'});
        const{data}=await uploadAPI.single(file);
        onChange(data.url);
        stopCamera();
        toast.success('Photo captured!');
      }catch{toast.error('Upload failed');}
      finally{setUploading(false);}
    },'image/jpeg',0.85);
  };
  const pickFile=async e=>{
    const file=e.target.files?.[0];if(!file)return;
    setUploading(true);
    try{
      const{data}=await uploadAPI.single(file);
      onChange(data.url);
      toast.success('Photo uploaded!');
    }catch{toast.error('Upload failed');}
    finally{setUploading(false);e.target.value='';}
  };

  return(
    <div style={{marginBottom:16}}>
      <div style={{fontSize:11,fontWeight:700,color:'var(--tm)',marginBottom:8,textTransform:'uppercase',letterSpacing:.4}}>
        Profile Photo / រូបថត
      </div>
      <div style={{display:'flex',gap:12,alignItems:'flex-start',flexWrap:'wrap'}}>
        {/* Preview */}
        <div style={{width:80,height:80,borderRadius:12,background:'var(--s2)',border:'2px solid var(--border)',
          overflow:'hidden',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',position:'relative'}}>
          {value
            ?<img src={value?.startsWith('http')?value:(window.location.protocol+'//'+window.location.hostname+':5000'+value)} alt="avatar" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
            :<ImageIcon size={28} color="var(--td)"/>}
          {value&&<button onClick={()=>onChange('')}
            style={{position:'absolute',top:2,right:2,width:18,height:18,borderRadius:'50%',
              background:'rgba(239,68,68,.9)',border:'none',cursor:'pointer',
              display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:10}}>✕</button>}
        </div>
        {/* Controls */}
        <div style={{flex:1,minWidth:140}}>
          {!streaming?(
            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
              <button type="button" onClick={startCamera}
                className="btn btn-ghost btn-sm" style={{gap:5}}>
                <Camera size={13}/> Camera
              </button>
              <button type="button" onClick={()=>fileRef.current?.click()}
                className="btn btn-ghost btn-sm" style={{gap:5}} disabled={uploading}>
                {uploading?<RefreshCw size={13} style={{animation:'spin 1s linear infinite'}}/>:<Upload size={13}/>}
                Upload
              </button>
              <input ref={fileRef} type="file" accept="image/*" style={{display:'none'}} onChange={pickFile}/>
            </div>
          ):(
            <div>
              <video ref={videoRef} autoPlay playsInline
                style={{width:180,height:135,borderRadius:8,objectFit:'cover',border:'2px solid var(--primary)',display:'block',marginBottom:6}}/>
              <canvas ref={canvasRef} style={{display:'none'}}/>
              <div style={{display:'flex',gap:6}}>
                <button type="button" onClick={snap} className="btn btn-primary btn-sm" style={{gap:5}} disabled={uploading}>
                  {uploading?<RefreshCw size={13} style={{animation:'spin 1s linear infinite'}}/>:<Camera size={13}/>} Snap
                </button>
                <button type="button" onClick={stopCamera} className="btn btn-ghost btn-sm">Cancel</button>
              </div>
            </div>
          )}
          <div style={{fontSize:10,color:'var(--td)',marginTop:6}}>JPG/PNG · max 5MB</div>
        </div>
      </div>
    </div>
  );
}

/* ── Document Uploader Component ───────────────────────────────── */
function DocumentUploader({docs=[], onChange}){
  const fileRef=useRef(null);
  const[uploading,setUploading]=useState(false);

  const docUrl = url => url?.startsWith('http') ? url : `${window.location.protocol}//${window.location.hostname}:5000${url}`;

  const upload=async e=>{
    const files=[...e.target.files];if(!files.length)return;
    setUploading(true);
    try{
      const results=await Promise.all(files.map(f=>uploadAPI.document(f)));
      const newDocs=results.map((r,i)=>({
        name:files[i].name,
        url:r.data.url,
        size:files[i].size,
        type:files[i].type,
        uploaded_at:new Date().toISOString(),
      }));
      onChange([...docs,...newDocs]);
      toast.success(`${newDocs.length} document(s) uploaded`);
    }catch{toast.error('Upload failed');}
    finally{setUploading(false);e.target.value='';}
  };

  const remove=idx=>onChange(docs.filter((_,i)=>i!==idx));

  const fmtSize=b=>b>1048576?`${(b/1048576).toFixed(1)}MB`:b>1024?`${(b/1024).toFixed(0)}KB`:`${b}B`;

  const fileIcon=type=>{
    if(type?.includes('pdf')) return '📄';
    if(type?.includes('word')||type?.includes('doc')) return '📝';
    if(type?.includes('excel')||type?.includes('sheet')||type?.includes('xls')) return '📊';
    if(type?.includes('image')) return '🖼️';
    return '📎';
  };

  return(
    <div>
      <div style={{fontSize:11,fontWeight:700,color:'var(--tm)',marginBottom:8,textTransform:'uppercase',letterSpacing:.4,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <span><Paperclip size={11} style={{marginRight:4}}/>Documents / ឯកសារ</span>
        <button type="button" onClick={()=>fileRef.current?.click()}
          className="btn btn-ghost btn-sm" style={{gap:4,fontSize:11}} disabled={uploading}>
          {uploading?<RefreshCw size={12} style={{animation:'spin 1s linear infinite'}}/>:<Upload size={12}/>}
          {uploading?'Uploading…':'Add File'}
        </button>
        <input ref={fileRef} type="file" multiple
          accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.txt"
          style={{display:'none'}} onChange={upload}/>
      </div>
      {docs.length===0&&(
        <div style={{border:'2px dashed var(--border)',borderRadius:8,padding:'20px',textAlign:'center',color:'var(--td)',fontSize:12,cursor:'pointer'}}
          onClick={()=>fileRef.current?.click()}>
          <Paperclip size={20} style={{margin:'0 auto 6px',display:'block',opacity:.4}}/>
          Click to upload documents<br/>
          <span style={{fontSize:10}}>PDF, Word, Excel, Images · max 10MB each</span>
        </div>
      )}
      {docs.length>0&&(
        <div style={{display:'flex',flexDirection:'column',gap:6}}>
          {docs.map((doc,i)=>(
            <div key={i} style={{display:'flex',alignItems:'center',gap:8,padding:'8px 10px',
              background:'var(--s2)',borderRadius:8,border:'1px solid var(--border)'}}>
              <span style={{fontSize:18,flexShrink:0}}>{fileIcon(doc.type)}</span>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{doc.name}</div>
                <div style={{fontSize:10,color:'var(--td)'}}>{fmtSize(doc.size||0)} · {doc.uploaded_at?new Date(doc.uploaded_at).toLocaleDateString():''}</div>
              </div>
              <a href={docUrl(doc.url)} target="_blank" rel="noreferrer"
                className="btn btn-ghost btn-sm" style={{padding:'3px 7px',flexShrink:0}} title="View">
                <Eye size={12}/>
              </a>
              <button type="button" onClick={()=>remove(i)}
                className="btn btn-ghost btn-sm" style={{padding:'3px 7px',flexShrink:0,color:'var(--danger)'}} title="Remove">
                <XIcon size={12}/>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function UsersPage(){
  const{t}=useLang();
  const{hasPermission,user:me}=useAuth();
  const isOwner=(me?.permissions||[]).length>=60;
  const myBranchId=me?.branch_id||null;
  const can={
    view:hasPermission('users.view'),
    create:hasPermission('users.create'),
    edit:hasPermission('users.edit'),
    delete:hasPermission('users.delete'),
    manageRoles:hasPermission('roles.manage'),
  };

  const[tab,setTab]=useState('users');
  const[viewMode,setViewMode]=useState('table');
  const[users,setUsers]=useState([]);const[roles,setRoles]=useState([]);const[branches,setBranches]=useState([]);
  const[ld,setLd]=useState(true);const[search,setSearch]=useState('');
  const[branchFilter,setBranchFilter]=useState('');const[roleFilter,setRoleFilter]=useState('');const[statusFilter,setStatusFilter]=useState('All');
  const[modal,setModal]=useState(null);const[form,setForm]=useState({});const[delUser,setDelUser]=useState(null);
  const[roleModal,setRoleModal]=useState(null);const[roleForm,setRoleForm]=useState({});const[delRole,setDelRole]=useState(null);
  const[auditLogs,setAuditLogs]=useState([]);const[auditLd,setAuditLd]=useState(false);
  const[auditFilter,setAuditFilter]=useState({entity_type:'',action:''});
  const[loginLogs,setLoginLogs]=useState([]);const[loginLd,setLoginLd]=useState(false);
  const[detailLog,setDetailLog]=useState(null);

  useEffect(()=>{
    branchAPI.getAll({limit:100}).then(r=>setBranches(r.data.data||[])).catch(()=>{});
    userAPI.getRoles({limit:100}).then(r=>setRoles(r.data.data||r.data||[])).catch(()=>{});
  },[]);

  const loadUsers=useCallback(async()=>{
    try{
      const p={search,limit:200};
      if(isOwner&&branchFilter)p.branch_id=branchFilter;
      if(roleFilter)p.role_id=roleFilter;
      if(statusFilter!=='All')p.is_active=statusFilter==='Active'?'true':'false';
      const r=await userAPI.getAll(p);
      setUsers(r.data.data||[]);
    }catch{toast.error('Failed to load users')}finally{setLd(false)};
  },[search,branchFilter,roleFilter,statusFilter,isOwner]);

  useEffect(()=>{if(tab==='users'){const t=setTimeout(loadUsers,300);return()=>clearTimeout(t)}},[loadUsers,tab]);

  const loadAudit=useCallback(async()=>{
    setAuditLd(true);
    try{const r=await auditAPI.getLogs({limit:100,...auditFilter});setAuditLogs(r.data.data||[])}
    catch{toast.error('Failed')}finally{setAuditLd(false)};
  },[auditFilter]);
  useEffect(()=>{if(tab==='audit')loadAudit()},[tab,loadAudit]);

  const loadLoginLogs=useCallback(async()=>{
    setLoginLd(true);
    try{const r=await auditAPI.getLoginLogs({limit:100});setLoginLogs(r.data.data||[])}
    catch{toast.error('Failed')}finally{setLoginLd(false)};
  },[]);
  useEffect(()=>{if(tab==='logins')loadLoginLogs()},[tab,loadLoginLogs]);

  const applyRoleTemplate=(roleName)=>{
    const tpl=ROLE_DEFAULTS[roleName];
    if(tpl){setRoleForm(f=>({...f,permissions:[...tpl]}));toast.success(`Loaded "${roleName}" template`);}
  };

  /* ─── User CRUD ─── */
  const userBlank={first_name:'',last_name:'',email:'',password:'',phone:'',role_id:'',branch_id:isOwner?'':(myBranchId?String(myBranchId):''),commission_rate:0,is_active:true,language:'en',avatar_url:'',documents:[],id_type:'national_id',id_number:'',id_expiry:'',id_front_url:'',id_back_url:'',nationality:'',date_of_birth:''};
  const F=(k,v)=>setForm(f=>({...f,[k]:v}));
  const isSameBranch=(u)=>isOwner||!myBranchId||u.branch_id===myBranchId;

  const saveUser=async()=>{
    if(!form.first_name||!form.email)return toast.error('Name & email required');
    if(modal==='new'&&!form.password)return toast.error('Password required');
    try{
      const d={...form,role_id:parseInt(form.role_id)||null,branch_id:parseInt(form.branch_id)||null,commission_rate:parseFloat(form.commission_rate)||0};
      if(!d.password)delete d.password;
      if(modal==='new'){await userAPI.create(d);toast.success('User created')}
      else{await userAPI.update(form.id,d);toast.success('User updated')}
      setModal(null);loadUsers();
    }catch(e){toast.error(e.response?.data?.error||'Failed')};
  };

  const toggleUserActive=async u=>{
    try{await userAPI.update(u.id,{is_active:!u.is_active});toast.success(u.is_active?'Deactivated':'Activated');loadUsers()}
    catch{toast.error('Failed')};
  };

  const doDeleteUser=async()=>{
    try{await userAPI.delete(delUser.id);toast.success('User deleted');setDelUser(null);setModal(null);loadUsers()}
    catch(e){toast.error(e.response?.data?.error||'Failed')};
  };

  /* ─── Role CRUD ─── */
  const roleBlank={name:'',description:'',permissions:[],is_system:false};
  const RF=(k,v)=>setRoleForm(f=>({...f,[k]:v}));
  const togglePerm=p=>setRoleForm(f=>{const ps=[...(f.permissions||[])];const i=ps.indexOf(p);i>=0?ps.splice(i,1):ps.push(p);return{...f,permissions:ps}});
  const toggleGroupAll=g=>setRoleForm(f=>{const ps=[...(f.permissions||[])];const gp=PERM_GROUPS[g];const all=gp.every(p=>ps.includes(p));return{...f,permissions:all?ps.filter(p=>!gp.includes(p)):[...new Set([...ps,...gp])]}});

  const saveRole=async()=>{
    if(!roleForm.name)return toast.error('Name required');
    try{
      if(roleModal==='new'){await userAPI.createRole(roleForm);toast.success('Role created')}
      else{await userAPI.updateRole(roleForm.id,roleForm);toast.success('Role updated')}
      setRoleModal(null);
      userAPI.getRoles({limit:100}).then(r=>setRoles(r.data.data||r.data||[])).catch(()=>{});
    }catch(e){toast.error(e.response?.data?.error||'Failed')};
  };

  const doDeleteRole=async()=>{
    try{await userAPI.deleteRole(delRole.id);toast.success('Role deleted');setDelRole(null);setModal(null);userAPI.getRoles({limit:100}).then(r=>setRoles(r.data.data||r.data||[])).catch(()=>{})}
    catch(e){toast.error(e.response?.data?.error||'Failed')};
  };

  const myBranchUsers=users.filter(u=>isSameBranch(u));
  const otherBranchUsers=users.filter(u=>!isSameBranch(u));

  const entityTypes=[{value:'',label:'All'},{value:'SaleOrder',label:'Sale Order'},{value:'SaleInvoice',label:'Invoice'},{value:'PosSession',label:'POS Session'},{value:'Commission',label:'Commission'},{value:'Product',label:'Product'},{value:'User',label:'User'}];
  const actionTypes=[{value:'',label:'All'},{value:'create',label:'Create'},{value:'update',label:'Update'},{value:'delete',label:'Delete'},{value:'status_change',label:'Status Change'},{value:'payment',label:'Payment'},{value:'pos_close',label:'POS Close'}];

  const buildRoleOpts=()=>[{value:'',label:'— Select Role —'},...roles.map(r=>({value:String(r.id),label:r.name}))];
  const buildBranchOpts=()=>{
    const list=isOwner?branches:branches.filter(b=>b.id===myBranchId);
    return[{value:'',label:isOwner?'— No Branch —':'(Locked to your branch)'},...list.map(b=>({value:String(b.id),label:b.name}))];
  };
  const buildBranchFilterOpts=()=>[{value:'',label:'All Branches'},...branches.map(b=>({value:String(b.id),label:b.name}))];
  const buildRoleFilterOpts=()=>[{value:'',label:'All Roles'},...roles.map(r=>({value:String(r.id),label:r.name}))];

  const TABS=[
    {id:'users',     label:t('users.tabs.users'),    icon:Users},
    {id:'roles',     label:t('users.tabs.roles'),    icon:Shield},
    {id:'matrix',    label:t('users.tabs.matrix'),   icon:LayoutGrid},
    {id:'audit',     label:t('users.tabs.audit'),    icon:ClipboardList},
    {id:'logins',    label:t('users.tabs.logins'),   icon:LogIn},
  ];

  return(<div>
    <PageHeader title={t('users.title')||"Users, Roles & Access Control"}
      subtitle={`${users.length} users · ${roles.length} roles`}
      action={
        tab==='users'&&can.create?<button className="btn btn-primary" onClick={()=>{setForm({...userBlank});setModal('new')}}><Plus size={16}/> {t('users.newUser')}</button>
        :tab==='roles'&&can.manageRoles?<button className="btn btn-primary" onClick={()=>{setRoleForm(roleBlank);setRoleModal('new')}}><Plus size={16}/> {t('users.createRole')}</button>
        :null
      }
    />

    {/* Branch scope indicator */}
    {!isOwner&&myBranchId&&(
      <div style={{display:'flex',alignItems:'center',gap:8,padding:'10px 14px',background:'var(--pm)',borderRadius:10,marginBottom:16,fontSize:13}}>
        <Building2 size={15} color="var(--primary)"/>
        <span style={{color:'var(--primary)',fontWeight:600}}>{t('users.branchScopedView')}</span>
        <span style={{color:'var(--text)'}}>{branches.find(b=>b.id===myBranchId)?.name||`Branch #${myBranchId}`}</span>
        <span style={{color:'var(--td)',fontSize:12}}>— You can only manage users in your assigned branch</span>
      </div>
    )}

    {/* Tab bar */}
    <div style={{display:'flex',gap:0,marginBottom:20,borderBottom:'1px solid var(--border)',overflowX:'auto'}}>
      {TABS.map(t=>(
        <button key={t.id} onClick={()=>setTab(t.id)}
          style={{display:'flex',alignItems:'center',gap:6,padding:'9px 16px',fontSize:13,fontWeight:600,
            border:'none',borderBottom:`2px solid ${tab===t.id?'var(--primary)':'transparent'}`,
            background:'transparent',cursor:'pointer',color:tab===t.id?'var(--primary)':'var(--td)',
            marginBottom:-1,transition:'color .15s',whiteSpace:'nowrap'}}>
          <t.icon size={14}/>{t.label}
        </button>
      ))}
    </div>

    {/* ═══ USERS TAB ═══ */}
    {tab==='users'&&<>
      <div className="filter-bar" style={{flexWrap:'wrap',gap:8}}>
        <div style={{flex:1,minWidth:200}}><SearchBar value={search} onChange={setSearch} placeholder="Search by name or email..."/></div>
        {isOwner&&<div style={{minWidth:160}}><FormInput value={branchFilter} onChange={setBranchFilter} options={buildBranchFilterOpts()}/></div>}
        <div style={{minWidth:140}}><FormInput value={roleFilter} onChange={setRoleFilter} options={buildRoleFilterOpts()}/></div>
        <FilterButtons options={['All','Active','Inactive']} active={statusFilter} onChange={setStatusFilter}/>
        <div style={{display:'flex',gap:6}}>
          <button className={`btn btn-ghost btn-sm`} style={viewMode==='table'?{background:'var(--pm)',color:'var(--primary)',borderColor:'var(--primary)'}:{}} onClick={()=>setViewMode('table')}>Table</button>
          <button className={`btn btn-ghost btn-sm`} style={viewMode==='grid'?{background:'var(--pm)',color:'var(--primary)',borderColor:'var(--primary)'}:{}} onClick={()=>setViewMode('grid')}>Cards</button>
        </div>
      </div>

      {viewMode==='grid'?(
        ld?<div style={{textAlign:'center',padding:40,color:'var(--td)'}}>{t('common.loading')}</div>:
        users.length===0?<div style={{textAlign:'center',padding:40,color:'var(--td)'}}>No users found</div>:(
          <div>
            {myBranchUsers.length>0&&(
              <div style={{marginBottom:24}}>
                {!isOwner&&<div style={{fontSize:11,fontWeight:700,color:'var(--td)',letterSpacing:.5,marginBottom:10}}>YOUR BRANCH USERS ({myBranchUsers.length})</div>}
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',gap:12}}>
                  {myBranchUsers.map(u=><UserCard key={u.id} u={u} isOwner={isOwner} currentBranchId={myBranchId} can={can}
                    onEdit={u=>{setForm({...u,role_id:u.role_id||u.role?.id||'',branch_id:u.branch_id||'',password:'',avatar_url:u.avatar_url||'',documents:u.documents||[]});setModal('edit')}}
                    onToggle={toggleUserActive} onDelete={u=>{setDelUser(u);setModal('delete')}}/>)}
                </div>
              </div>
            )}
            {otherBranchUsers.length>0&&(
              <div>
                <div style={{fontSize:11,fontWeight:700,color:'var(--danger)',letterSpacing:.5,marginBottom:10,display:'flex',alignItems:'center',gap:6}}><Lock size={12}/> OTHER BRANCH USERS — RESTRICTED ({otherBranchUsers.length})</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',gap:12,opacity:.7}}>
                  {otherBranchUsers.map(u=><UserCard key={u.id} u={u} isOwner={false} currentBranchId={myBranchId} can={{}} onEdit={()=>{}} onToggle={()=>{}} onDelete={()=>{}}/>)}
                </div>
              </div>
            )}
          </div>
        )
      ):(
        <DataTable loading={ld} columns={[
          {key:'name',label:'User',render:r=>(
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <div style={{width:32,height:32,borderRadius:8,background:r.is_active?'var(--pm)':'var(--s3)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:800,color:r.is_active?'var(--primary)':'var(--td)',flexShrink:0}}>
                {(r.first_name?.[0]||'')+(r.last_name?.[0]||'')}
              </div>
              <div>
                <div style={{fontWeight:700}}>{r.first_name} {r.last_name}</div>
                <div style={{fontSize:11,color:'var(--td)'}}>{r.email}</div>
              </div>
            </div>
          )},
          {key:'role',label:'Role',render:r=>{
            const same=isSameBranch(r);
            return(
              <div>
                {can.edit&&same&&can.manageRoles?(
                  <select value={String(r.role_id||r.role?.id||'')}
                    onChange={async e=>{
                      const newRoleId=e.target.value?parseInt(e.target.value):null;
                      try{
                        const updated=await userAPI.assignRole(r.id,newRoleId);
                        setUsers(prev=>prev.map(u=>u.id===r.id?updated.data:u));
                        toast.success('Role updated');
                      }catch(err){toast.error(err.response?.data?.error||'Failed');}
                    }}
                    style={{fontSize:11,padding:'3px 6px',borderRadius:6,border:'1.5px solid var(--border)',
                      background:'var(--s2)',color:'var(--text)',cursor:'pointer',maxWidth:140}}
                    onClick={e=>e.stopPropagation()}>
                    <option value="">— No Role —</option>
                    {roles.map(ro=><option key={ro.id} value={ro.id}>{ro.name}</option>)}
                  </select>
                ):(
                  <span style={{fontSize:11,fontWeight:700,color:`var(--${ROLE_BADGE[r.role?.name]||'primary'})`,background:`var(--${ROLE_BADGE[r.role?.name]||'primary'}m)`,padding:'2px 8px',borderRadius:5}}>{r.role?.name||'—'}</span>
                )}
                {r.role&&<div style={{fontSize:10,color:'var(--td)',marginTop:2}}>{(r.role.permissions||[]).length} permissions</div>}
              </div>
            );
          }},
          {key:'branch',label:'Branch / Scope',render:r=>(
            <div>
              <BranchTag branch={r.branch} locked={!isSameBranch(r)}/>
              {r.branch&&<div style={{fontSize:10,color:'var(--td)',marginTop:2}}>Branch-scoped</div>}
              {!r.branch&&isOwner&&<div style={{fontSize:10,color:'var(--warning)',marginTop:2}}>No branch assigned</div>}
            </div>
          )},
          {key:'phone',label:'Phone',render:r=>r.phone||'—'},
          {key:'commission_rate',label:'Comm.',render:r=>r.commission_rate>0?<span style={{color:'var(--warning)',fontWeight:600}}>{r.commission_rate}%</span>:'—'},
          {key:'last_login',label:'Last Login',render:r=>r.last_login?new Date(r.last_login).toLocaleString('en-US',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}):<span style={{color:'var(--td)'}}>Never</span>},
          {key:'is_active',label:'Status',render:r=><Badge variant={r.is_active?'success':'danger'}>{r.is_active?'Active':'Inactive'}</Badge>},
        ]} data={users} actions={r=>{
          const same=isSameBranch(r);
          return(
            <div className="flex gap-2">
              {same&&can.edit&&<button className="btn btn-ghost btn-sm" onClick={()=>toggleUserActive(r)} title={r.is_active?'Deactivate':'Activate'}>{r.is_active?<ToggleRight size={15} color="var(--success)"/>:<ToggleLeft size={15}/>}</button>}
              {same&&can.edit&&<button className="btn btn-ghost btn-sm" onClick={()=>{setForm({...r,role_id:r.role_id||r.role?.id||'',branch_id:r.branch_id||r.branch?.id||'',password:'',avatar_url:r.avatar_url||'',documents:r.documents||[]});setModal('edit')}}><Edit size={14}/></button>}
              {same&&can.delete&&<button className="btn btn-danger btn-sm" onClick={()=>{setDelUser(r);setModal('delete')}}><Trash2 size={14}/></button>}
              {!same&&<span style={{fontSize:11,color:'var(--danger)',display:'flex',alignItems:'center',gap:4,padding:'4px 8px'}}><Lock size={11}/> Restricted</span>}
            </div>
          );
        }}/>
      )}
    </>}

    {/* ═══ ROLES TAB ═══ */}
    {tab==='roles'&&<div style={{display:'grid',gap:14}}>
      {roles.map(role=>{
        const pc=(role.permissions||[]).length;
        const ownerLevel=pc>=ALL_PERMS.length-2;
        const roleUsers=users.filter(u=>(u.role_id===role.id)||(u.role?.id===role.id));
        return(
          <div key={role.id} className="card" style={{padding:0,overflow:'hidden'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'14px 18px',background:'var(--s2)'}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:700,fontSize:15,display:'flex',alignItems:'center',gap:8}}>
                  {ownerLevel?<Crown size={16} color="#d97706"/>:<Shield size={16} color="var(--primary)"/>}
                  {role.name}
                  {ownerLevel&&<OwnerOnlyBadge/>}
                </div>
                <div style={{fontSize:12,color:'var(--td)',marginTop:2}}>
                  {role.description||`${pc} of ${ALL_PERMS.length} permissions`}
                  {' · '}
                  <span style={{color:ownerLevel?'#d97706':'var(--primary)',fontWeight:600}}>
                    {ownerLevel?'Global (all branches)':'Branch-scoped'}
                  </span>
                  {roleUsers.length>0&&<span style={{color:'var(--td)'}}> · <span style={{color:'var(--text)',fontWeight:600}}>{roleUsers.length}</span> user{roleUsers.length!==1?'s':''}</span>}
                </div>
                {/* Users assigned to this role */}
                {roleUsers.length>0&&(
                  <div style={{display:'flex',flexWrap:'wrap',gap:4,marginTop:8}}>
                    {roleUsers.slice(0,8).map(u=>(
                      <div key={u.id} style={{display:'flex',alignItems:'center',gap:4,padding:'2px 8px',borderRadius:12,background:'var(--pm)',border:'1px solid var(--primary)22',fontSize:11}}>
                        <div style={{width:16,height:16,borderRadius:'50%',background:'var(--primary)',color:'#fff',fontSize:8,fontWeight:800,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                          {(u.first_name?.[0]||'')+(u.last_name?.[0]||'')}
                        </div>
                        <span style={{color:'var(--primary)',fontWeight:600}}>{u.first_name} {u.last_name}</span>
                        {u.branch&&<span style={{color:'var(--td)',fontSize:10}}>· {u.branch.name}</span>}
                      </div>
                    ))}
                    {roleUsers.length>8&&<span style={{fontSize:11,color:'var(--td)',padding:'2px 6px'}}>+{roleUsers.length-8} more</span>}
                  </div>
                )}
              </div>
              <div style={{display:'flex',gap:6,alignItems:'center',flexShrink:0,marginLeft:12}}>
                <Badge variant={role.is_system?'accent':'primary'}>{role.is_system?'System':'Custom'}</Badge>
                <Badge variant={pc>=ALL_PERMS.length?'success':pc>30?'info':pc>10?'warning':'danger'}>{pc}/{ALL_PERMS.length}</Badge>
                {can.manageRoles&&<button className="btn btn-ghost btn-sm" onClick={()=>{setRoleForm({...role});setRoleModal('edit')}}><Edit size={14}/></button>}
                {can.manageRoles&&!role.is_system&&<button className="btn btn-danger btn-sm" onClick={()=>{setDelRole(role);setModal('delrole')}}><Trash2 size={14}/></button>}
              </div>
            </div>
            <div style={{padding:'12px 18px'}}>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(190px,1fr))',gap:8}}>
                {Object.entries(PERM_GROUPS).map(([group,perms])=>{
                  const has=perms.filter(p=>(role.permissions||[]).includes(p)).length;
                  return(
                    <div key={group} style={{padding:8,borderRadius:6,background:has===perms.length?'var(--sm)':has>0?'var(--wm)':'var(--s2)',border:`1px solid ${has===perms.length?'rgba(34,197,94,.2)':has>0?'rgba(245,158,11,.2)':'var(--border)'}`}}>
                      <div style={{fontSize:11,fontWeight:700,color:has===perms.length?'var(--success)':has>0?'var(--warning)':'var(--td)',marginBottom:4}}>
                        {group} ({has}/{perms.length})
                      </div>
                      <div style={{display:'flex',flexWrap:'wrap',gap:3}}>
                        {perms.map(p=>{
                          const h=(role.permissions||[]).includes(p);
                          const ownerOnly=OWNER_ONLY_PERMS.includes(p);
                          return(
                            <span key={p} style={{fontSize:9,padding:'1px 5px',borderRadius:3,
                              background:h?'var(--sm)':'transparent',
                              color:h?'var(--success)':'var(--td)',fontWeight:h?600:400,
                              display:'flex',alignItems:'center',gap:2}}>
                              {h?'✓':'✕'} {p.split('.')[1]}
                              {ownerOnly&&h&&<Crown size={7} color="#d97706"/>}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>}

    {/* ═══ ACCESS MATRIX TAB ═══ */}
    {tab==='matrix'&&<ModuleMatrixTab roles={roles}/>}

    {/* ═══ AUDIT LOGS TAB ═══ */}
    {tab==='audit'&&<>
      <div className="filter-bar">
        <div style={{minWidth:160}}><FormInput value={auditFilter.entity_type} onChange={v=>setAuditFilter(f=>({...f,entity_type:v}))} options={entityTypes}/></div>
        <div style={{minWidth:140}}><FormInput value={auditFilter.action} onChange={v=>setAuditFilter(f=>({...f,action:v}))} options={actionTypes}/></div>
        <button className="btn btn-ghost btn-sm" onClick={loadAudit}>Refresh</button>
      </div>
      <DataTable loading={auditLd} columns={[
        {key:'time',label:'Time',render:r=>r.created_at?new Date(r.created_at).toLocaleString('en-US',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit',second:'2-digit'}):'—'},
        {key:'user',label:'User',render:r=>r.user?<span style={{fontWeight:700}}>{r.user.first_name} {r.user.last_name}</span>:'System'},
        {key:'action',label:'Action',render:r=>{const c={create:'success',update:'info',status_change:'warning',payment:'info',pos_close:'accent',delete:'danger'};return<Badge variant={c[r.action]||'primary'}>{r.action}</Badge>}},
        {key:'entity_type',label:'Entity',render:r=><Badge variant="primary">{r.entity_type||'—'}</Badge>},
        {key:'entity_id',label:'ID',render:r=>r.entity_id?`#${r.entity_id}`:'—'},
        {key:'description',label:'Description',render:r=><span style={{maxWidth:300,overflow:'hidden',textOverflow:'ellipsis',display:'block'}}>{r.description||'—'}</span>},
        {key:'ip_address',label:'IP'},
      ]} data={auditLogs} actions={r=>r.old_values||r.new_values?(<button className="btn btn-ghost btn-sm" onClick={()=>setDetailLog(r)}><Eye size={14}/></button>):null}/>
    </>}

    {/* ═══ LOGIN HISTORY TAB ═══ */}
    {tab==='logins'&&<DataTable loading={loginLd} columns={[
      {key:'time',label:'Time',render:r=>r.created_at?new Date(r.created_at).toLocaleString('en-US',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit',second:'2-digit'}):'—'},
      {key:'user',label:'User',render:r=>r.user?<div><div style={{fontWeight:700}}>{r.user.first_name} {r.user.last_name}</div><div style={{fontSize:11,color:'var(--td)'}}>{r.user.email}</div></div>:'Unknown'},
      {key:'action',label:'Action',render:r=><Badge variant={r.action==='login'?'success':r.action==='logout'?'info':'danger'}>{r.action}</Badge>},
      {key:'ip_address',label:'IP Address'},
      {key:'user_agent',label:'Device',render:r=><span style={{fontSize:11,color:'var(--td)',maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',display:'block'}}>{r.user_agent||'—'}</span>},
    ]} data={loginLogs}/>}

    {/* ═══ USER MODAL ═══ */}
    <Modal open={modal==='new'||modal==='edit'} onClose={()=>setModal(null)} title={modal==='new'?'Add User':'Edit User'} width={640}>
      {!isOwner&&myBranchId&&(
        <div style={{display:'flex',alignItems:'center',gap:8,padding:'9px 12px',background:'var(--pm)',borderRadius:8,marginBottom:14,fontSize:12,color:'var(--primary)'}}>
          <Building2 size={14}/> This user will be assigned to <strong>{branches.find(b=>b.id===myBranchId)?.name||`Branch #${myBranchId}`}</strong>
        </div>
      )}

      {/* Photo */}
      <PhotoCapture value={form.avatar_url||''} onChange={v=>F('avatar_url',v)}/>

      <div style={{borderTop:'1px solid var(--border)',margin:'4px 0 16px'}}/>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 12px'}}>
        <FormInput label="First Name *" value={form.first_name||''} onChange={v=>F('first_name',v)} required/>
        <FormInput label="Last Name" value={form.last_name||''} onChange={v=>F('last_name',v)}/>
      </div>
      <FormInput label="Email *" value={form.email||''} onChange={v=>F('email',v)} type="email" required/>
      <FormInput label={modal==='new'?'Password *':'New Password (leave blank = no change)'} value={form.password||''} onChange={v=>F('password',v)} type="password" required={modal==='new'}/>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 12px'}}>
        <FormInput label="Phone" value={form.phone||''} onChange={v=>F('phone',v)}/>
        <div>
          <FormInput label="Role *" value={String(form.role_id||'')} onChange={v=>{
            if(!isOwner&&myBranchId){setForm(f=>({...f,role_id:v,branch_id:String(myBranchId)}))}
            else setForm(f=>({...f,role_id:v}));
          }} options={buildRoleOpts()} required/>
          {form.role_id&&(()=>{
            const r=roles.find(x=>x.id===parseInt(form.role_id));
            if(!r)return null;
            const pc=(r.permissions||[]).length;
            const ownerLevel=pc>=ALL_PERMS.length-2;
            return(
              <div style={{fontSize:11,marginTop:-8,marginBottom:8,display:'flex',alignItems:'center',gap:6,
                padding:'5px 8px',borderRadius:6,
                background:ownerLevel?'#fef3c7':'var(--pm)',
                color:ownerLevel?'#d97706':'var(--primary)'}}>
                {ownerLevel?<Crown size={11}/>:<Shield size={11}/>}
                {pc} permissions · {ownerLevel?'Full global access':'Branch-scoped'}
              </div>
            );
          })()}
        </div>
        <div>
          <FormInput label="Branch" value={String(form.branch_id||'')} onChange={v=>F('branch_id',v)} options={buildBranchOpts()} disabled={!isOwner&&!!myBranchId}/>
          {!isOwner&&myBranchId&&<div style={{fontSize:10,color:'var(--td)',marginTop:-8,marginBottom:8,display:'flex',alignItems:'center',gap:4}}><Lock size={9}/> Locked to your branch</div>}
        </div>
        <FormInput label="Commission %" value={String(form.commission_rate||0)} onChange={v=>F('commission_rate',v)} type="number"/>
        <FormInput label="Language" value={form.language||'en'} onChange={v=>F('language',v)} options={[{value:'en',label:'English'},{value:'km',label:'ខ្មែរ (Khmer)'}]}/>
        <FormInput label="Status" value={form.is_active?'true':'false'} onChange={v=>F('is_active',v==='true')} options={[{value:'true',label:'Active'},{value:'false',label:'Inactive'}]}/>
      </div>

      {/* Documents */}
      <div style={{borderTop:'1px solid var(--border)',margin:'12px 0'}}/>
      <DocumentUploader docs={form.documents||[]} onChange={v=>F('documents',v)}/>

      {/* Identity Card / Passport */}
      <div style={{borderTop:'1px solid var(--border)',margin:'16px 0 12px'}}/>
      <div style={{fontSize:11,fontWeight:700,color:'var(--tm)',marginBottom:12,textTransform:'uppercase',letterSpacing:.4,display:'flex',alignItems:'center',gap:6}}>
        <span>🪪</span> Identity Document / ឯកសារអត្តសញ្ញាណ
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 12px'}}>
        <FormInput label="ID Type / ប្រភេទ" value={form.id_type||'national_id'} onChange={v=>F('id_type',v)}
          options={[
            {value:'national_id',label:'🪪 National ID / អត្តសញ្ញាណបណ្ណ'},
            {value:'passport',label:'📘 Passport / លិខិតឆ្លងដែន'},
            {value:'driver_license',label:'🚗 Driver License / អាជ្ញាប័ណ្ណបើកបរ'},
            {value:'other',label:'📄 Other / ផ្សេងៗ'},
          ]}/>
        <FormInput label="ID Number / លេខ" value={form.id_number||''} onChange={v=>F('id_number',v)} placeholder="e.g. 0123456789"/>
        <FormInput label="Expiry Date / ថ្ងៃផុតកំណត់" value={form.id_expiry||''} onChange={v=>F('id_expiry',v)} type="date"/>
        <FormInput label="Nationality / សញ្ជាតិ" value={form.nationality||''} onChange={v=>F('nationality',v)} placeholder="e.g. Cambodian"/>
        <FormInput label="Date of Birth / ថ្ងៃខែឆ្នាំកំណើត" value={form.date_of_birth||''} onChange={v=>F('date_of_birth',v)} type="date"/>
      </div>

      {/* ID Card Photos */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginTop:4}}>
        {[
          ['id_front_url','Front of ID / មុខ','📷 Front'],
          ['id_back_url','Back of ID / ខាងក្រោយ','📷 Back'],
        ].map(([field,label,btnLabel])=>{
          const val=form[field]||'';
          const fullUrl=val?(val.startsWith('http')?val:(window.location.protocol+'//'+window.location.hostname+':5000'+val)):'';
          return(
            <div key={field}>
              <div style={{fontSize:11,fontWeight:600,color:'var(--tm)',marginBottom:6}}>{label}</div>
              <div style={{position:'relative',width:'100%',height:90,borderRadius:8,
                border:`2px dashed ${val?'var(--primary)':'var(--border)'}`,
                background:'var(--s2)',overflow:'hidden',cursor:'pointer'}}
                onClick={()=>document.getElementById(`id-upload-${field}`)?.click()}>
                {val
                  ?<img src={fullUrl} alt={label} style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                  :<div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100%',color:'var(--td)',gap:4}}>
                    <Upload size={18}/>
                    <span style={{fontSize:10}}>{btnLabel}</span>
                  </div>}
                {val&&<button type="button" onClick={e=>{e.stopPropagation();F(field,'');}}
                  style={{position:'absolute',top:4,right:4,width:20,height:20,borderRadius:'50%',
                    background:'rgba(239,68,68,.9)',border:'none',cursor:'pointer',color:'#fff',fontSize:11,
                    display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>}
              </div>
              <input id={`id-upload-${field}`} type="file" accept="image/*,.pdf"
                style={{display:'none'}}
                onChange={async e=>{
                  const file=e.target.files?.[0];if(!file)return;
                  try{const{data}=await uploadAPI.single(file);F(field,data.url);toast.success('Uploaded!');}
                  catch{toast.error('Upload failed');}
                  e.target.value='';
                }}/>
            </div>
          );
        })}
      </div>

      <div className="modal-footer" style={{marginTop:16}}>
        <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
        <button className="btn btn-primary" onClick={saveUser}><UserCheck size={14}/> {modal==='new'?'Create User':'Save Changes'}</button>
      </div>
    </Modal>

    {/* ═══ ROLE MODAL ═══ */}
    <Modal open={roleModal==='new'||roleModal==='edit'} onClose={()=>setRoleModal(null)} title={roleModal==='new'?t('users.createRole'):t('users.editRole')} width={820}>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 12px',marginBottom:12}}>
        <FormInput label={t('users.roleName')||"Role Name *"} value={roleForm.name||''} onChange={v=>RF('name',v)} required placeholder="e.g. Warehouse Manager"/>
        <FormInput label={t('users.roleDescription')||"Description"} value={roleForm.description||''} onChange={v=>RF('description',v)} placeholder="Brief description"/>
      </div>

      {/* Owner-only warning */}
      <div style={{display:'flex',alignItems:'center',gap:8,padding:'9px 12px',background:'#fef3c7',borderRadius:8,marginBottom:12,fontSize:12,color:'#d97706'}}>
        <Crown size={14}/> Permissions marked <strong>OWNER ONLY</strong> are shown for reference only — they are enforced at the server level and grant cross-branch access.
      </div>

      {/* Templates */}
      <div style={{marginBottom:14,padding:'10px 12px',background:'var(--s2)',borderRadius:8}}>
        <div style={{fontSize:11,fontWeight:700,color:'var(--td)',marginBottom:8,letterSpacing:.5}}>{t('users.quickTemplate')||'QUICK LOAD FROM TEMPLATE'}</div>
        <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
          {Object.keys(ROLE_DEFAULTS).map(name=>(
            <button key={name} className="btn btn-ghost btn-sm" onClick={()=>applyRoleTemplate(name)} style={{fontSize:11}}>{name}</button>
          ))}
        </div>
      </div>

      {/* Permission toggles */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
        <div>
          <span style={{fontSize:11,fontWeight:700,color:'var(--td)',letterSpacing:.5}}>PERMISSIONS</span>
          <span style={{color:'var(--td)',marginLeft:8,fontSize:12}}>({(roleForm.permissions||[]).length}/{ALL_PERMS.length} selected)</span>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-ghost btn-sm" onClick={()=>setRoleForm(f=>({...f,permissions:[...ALL_PERMS]}))}>All</button>
          <button className="btn btn-ghost btn-sm" onClick={()=>setRoleForm(f=>({...f,permissions:[]}))}>None</button>
        </div>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:10,maxHeight:440,overflowY:'auto',padding:4}}>
        {Object.entries(PERM_GROUPS).map(([group,perms])=>{
          const sel=perms.filter(p=>(roleForm.permissions||[]).includes(p)).length;
          const all=sel===perms.length;
          const hasOwnerOnly=perms.some(p=>OWNER_ONLY_PERMS.includes(p));
          return(
            <div key={group} style={{padding:10,borderRadius:8,background:'var(--s2)',border:'1px solid var(--border)'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8,cursor:'pointer'}} onClick={()=>toggleGroupAll(group)}>
                <span style={{fontSize:12,fontWeight:700,color:all?'var(--success)':sel>0?'var(--warning)':'var(--td)',display:'flex',alignItems:'center',gap:5}}>
                  {group}
                  {hasOwnerOnly&&<OwnerOnlyBadge/>}
                </span>
                <span style={{fontSize:10,color:'var(--td)'}}>{sel}/{perms.length}</span>
              </div>
              {perms.map(p=>{
                const checked=(roleForm.permissions||[]).includes(p);
                return(
                  <PermCheck key={p} perm={p} checked={checked} onChange={()=>togglePerm(p)}/>
                );
              })}
            </div>
          );
        })}
      </div>
      <div className="modal-footer" style={{marginTop:16}}>
        <button className="btn btn-ghost" onClick={()=>setRoleModal(null)}>{t('common.cancel')}</button>
        <button className="btn btn-primary" onClick={saveRole}>{t('common.save')}</button>
      </div>
    </Modal>

    {/* ═══ DELETE USER ═══ */}
    <Modal open={modal==='delete'&&!!delUser} onClose={()=>{setModal(null);setDelUser(null)}} title="Delete User" width={420}>
      {delUser&&(
        <div style={{textAlign:'center',padding:'10px 0 18px'}}>
          <div style={{width:52,height:52,borderRadius:13,background:'var(--dm)',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 14px'}}><AlertTriangle size={22} color="var(--danger)"/></div>
          <div style={{fontSize:15,fontWeight:700,marginBottom:6}}>Delete {delUser.first_name} {delUser.last_name}?</div>
          <div style={{fontSize:13,color:'var(--tm)',marginBottom:4}}>{delUser.email}</div>
          <div style={{fontSize:12,color:'var(--td)'}}>This action cannot be undone.</div>
        </div>
      )}
      <div className="modal-footer">
        <button className="btn btn-ghost" onClick={()=>{setModal(null);setDelUser(null)}}>Cancel</button>
        <button className="btn btn-danger" onClick={doDeleteUser}><Trash2 size={14}/> Delete User</button>
      </div>
    </Modal>

    {/* ═══ DELETE ROLE ═══ */}
    <Modal open={modal==='delrole'&&!!delRole} onClose={()=>{setModal(null);setDelRole(null)}} title="Delete Role" width={420}>
      {delRole&&(
        <div style={{textAlign:'center',padding:'10px 0 18px'}}>
          <div style={{width:52,height:52,borderRadius:13,background:'var(--dm)',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 14px'}}><AlertTriangle size={22} color="var(--danger)"/></div>
          <div style={{fontSize:15,fontWeight:700,marginBottom:6}}>Delete role "{delRole.name}"?</div>
          <div style={{fontSize:12,color:'var(--td)'}}>Users with this role will lose their permissions. This cannot be undone.</div>
        </div>
      )}
      <div className="modal-footer">
        <button className="btn btn-ghost" onClick={()=>{setModal(null);setDelRole(null)}}>Cancel</button>
        <button className="btn btn-danger" onClick={doDeleteRole}><Trash2 size={14}/> Delete Role</button>
      </div>
    </Modal>

    {/* ═══ AUDIT DETAIL ═══ */}
    <Modal open={!!detailLog} onClose={()=>setDetailLog(null)} title="Audit Log Detail" width={600}>
      {detailLog&&<>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14}}>
          {[['User',`${detailLog.user?.first_name||''} ${detailLog.user?.last_name||''}`],['Action',detailLog.action],['Entity',`${detailLog.entity_type} #${detailLog.entity_id}`],['IP',detailLog.ip_address||'—'],['Description',detailLog.description||'—']].map(([k,v])=>(
            <div key={k} style={{gridColumn:k==='Description'?'span 2':'',background:'var(--s2)',borderRadius:8,padding:'8px 12px'}}>
              <div style={{fontSize:10,color:'var(--td)',fontWeight:600,letterSpacing:.4}}>{k.toUpperCase()}</div>
              <div style={{fontSize:13,marginTop:2}}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          {[['Old Values','var(--danger)',detailLog.old_values],['New Values','var(--success)',detailLog.new_values]].map(([lbl,c,val])=>(
            <div key={lbl}>
              <div style={{fontSize:12,fontWeight:700,color:c,marginBottom:6}}>{lbl}</div>
              <pre style={{padding:12,background:'var(--s2)',borderRadius:8,fontSize:11,overflow:'auto',maxHeight:180,color:'var(--tm)',whiteSpace:'pre-wrap'}}>{val?JSON.stringify(val,null,2):'—'}</pre>
            </div>
          ))}
        </div>
      </>}
    </Modal>
  </div>);
}
